import { useEffect, useState } from 'react';
import type { BuildInfo } from '../../build-info';
import { desktopBridge } from './bridge';

export type { BuildInfo };

// Set by vite.config.ts at build time.
declare const __HIVE_BUILD__: BuildInfo | undefined;

/** The commit and time this copy of the app was built from. */
export const BUILD: BuildInfo | null = typeof __HIVE_BUILD__ === 'undefined' ? null : __HIVE_BUILD__;

const REPO = 'https://github.com/the-hive-games/hive';

export const shortCommit = (commit: string) => commit.slice(0, 7);

export const commitUrl = (commit: string) => `${REPO}/commit/${commit}`;

/** "26 Oct 01:36", in the viewer's time zone. */
export function builtAt(time: string): string {
  const d = new Date(time);
  if (Number.isNaN(d.getTime())) return '';
  const day = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  const clock = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${day} ${clock}`;
}

/** Whether `latest` (from the site's version.json) is a different build from the one running. */
export function isNewerBuild(current: BuildInfo, latest: unknown): boolean {
  const commit = (latest as { commit?: unknown } | null)?.commit;
  return (
    typeof commit === 'string' && commit !== 'unknown' && current.commit !== 'unknown' && commit !== current.commit
  );
}

const CHECK_EVERY_MS = 5 * 60_000;

const fetchLiveVersion = async (): Promise<unknown> =>
  (await fetch('/version.json', { cache: 'no-store' })).json() as Promise<unknown>;

/**
 * True once the hosted site is serving a newer build than this one, so an open
 * tab (or the installed phone app) can offer a reload. Checks on focus and
 * every few minutes. Off in development and in the desktop app, which ships
 * its own copy.
 */
export function useUpdateReady(
  current: BuildInfo | null = BUILD,
  fetchVersion: () => Promise<unknown> = fetchLiveVersion,
  enabled: boolean = import.meta.env.PROD && !desktopBridge(),
): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!enabled || !current || ready) return;
    let stopped = false;
    const check = () => {
      if (document.visibilityState === 'hidden') return;
      fetchVersion()
        .then((latest) => {
          if (!stopped && isNewerBuild(current, latest)) setReady(true);
        })
        .catch(() => {
          // Offline or mid-deploy: try again next time.
        });
    };
    check();
    const timer = window.setInterval(check, CHECK_EVERY_MS);
    document.addEventListener('visibilitychange', check);
    return () => {
      stopped = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', check);
    };
  }, [current, fetchVersion, enabled, ready]);
  return ready;
}
